import { useQuery } from '@tanstack/react-query'
import type { AxiosInstance } from 'axios'
import { useApplicationContext } from '@mysaiva/core/application'
import { errorStatus } from '../http/errorStatus'
import { isUsageEmpty, toUsageSummary } from './usage.types'
import type { UsageStatus, UsageSummary, UsageSummaryDto } from './usage.types'

/**
 * Org-scoped usage aggregation (`OrgUsageSummaryRead`). The backend
 * defaults `environment` to `production`; the dashboard shows that one.
 * Older backends answer 404 and the dashboard hides the usage row.
 */
export function usageSummaryPath(orgId: string): string {
  return `integrations/v1/orgs/${encodeURIComponent(orgId)}/api-keys/usage`
}
export const USAGE_PERIOD_DAYS = 30

/** HTTP statuses that mean "this backend does not serve usage yet". */
const UNAVAILABLE_STATUSES = new Set([404, 501])

export const usageSummaryQueryKey = (orgId: string) =>
  ['developer-platform', 'usage-summary', orgId, USAGE_PERIOD_DAYS] as const

/** `null` means the endpoint is not available on this backend. */
export async function fetchUsageSummary(
  apiClient: AxiosInstance,
  orgId: string
): Promise<UsageSummary | null> {
  try {
    const { data } = await apiClient.get<UsageSummaryDto>(usageSummaryPath(orgId), {
      params: { period_days: USAGE_PERIOD_DAYS }
    })
    return toUsageSummary(data)
  } catch (error) {
    if (UNAVAILABLE_STATUSES.has(errorStatus(error) ?? -1)) {
      return null
    }
    throw error
  }
}

export interface UsageSummaryState {
  status: UsageStatus
  data: UsageSummary | null
  error: unknown
  refetch: () => void
}

/**
 * Usage figures for the selected organisation, folded into the five UI
 * states the dashboard renders (loading / available / empty / unavailable /
 * error). Re-runs when the organisation switcher changes.
 */
export function useUsageSummary(): UsageSummaryState {
  const { apiClient, orgContext } = useApplicationContext()
  const orgId = orgContext.org.id

  const query = useQuery({
    queryKey: usageSummaryQueryKey(orgId),
    queryFn: () => fetchUsageSummary(apiClient, orgId),
    staleTime: 60_000
  })

  let status: UsageStatus
  if (query.isPending) status = 'loading'
  else if (query.isError) status = 'error'
  else if (query.data === null) status = 'unavailable'
  else status = isUsageEmpty(query.data) ? 'empty' : 'available'

  return {
    status,
    data: query.data ?? null,
    error: query.error,
    refetch: () => {
      void query.refetch()
    }
  }
}
